import { supabase } from '../supabaseClient';

const DEFAULT_RESTAURANT_NAME = 'Restaurant';
const DEFAULT_RECEIPT_FOOTER = 'A Product By Smart Solutions';
const DEFAULT_DELIVERY_CHARGE = 150;
const DEFAULT_OPEN_TIME = '15:00';
const DEFAULT_CLOSE_TIME = '03:00';

const SETTINGS_SELECT = 'display_name,branch_name,phone,address,logo_url,logo_storage_path,receipt_footer,public_ordering_title,tax_label,tax_number,delivery_charge,store_open_time,store_close_time,is_store_open,public_tagline,public_about_text,public_hero_images';
const LEGACY_SETTINGS_SELECT = 'display_name,branch_name,phone,address,logo_url,receipt_footer,public_ordering_title,tax_label,tax_number';

const buildBrand = ({
  name = DEFAULT_RESTAURANT_NAME,
  displayName = name,
  branchName = '',
  phone = '',
  address = '',
  logoUrl = '',
  logoStoragePath = '',
  receiptFooter = DEFAULT_RECEIPT_FOOTER,
  publicOrderingTitle = displayName,
  taxLabel = '',
  taxNumber = '',
  deliveryCharge = DEFAULT_DELIVERY_CHARGE,
  storeOpenTime = DEFAULT_OPEN_TIME,
  storeCloseTime = DEFAULT_CLOSE_TIME,
  isStoreOpen = true,
  tagline = '',
  aboutText = '',
  heroImages = [],
} = {}) => ({
  name,
  displayName,
  branchName,
  phone,
  address,
  logoUrl,
  logoStoragePath,
  receiptFooter,
  publicOrderingTitle,
  taxLabel,
  taxNumber,
  deliveryCharge: Number(deliveryCharge ?? DEFAULT_DELIVERY_CHARGE),
  storeOpenTime: storeOpenTime || DEFAULT_OPEN_TIME,
  storeCloseTime: storeCloseTime || DEFAULT_CLOSE_TIME,
  isStoreOpen: isStoreOpen !== false,
  tagline,
  aboutText,
  heroImages: Array.isArray(heroImages) ? heroImages.filter(Boolean) : [],
});

export const sanitizeFilePart = (value) => (
  String(value || DEFAULT_RESTAURANT_NAME)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || DEFAULT_RESTAURANT_NAME
);

const fetchRestaurantSettings = async (restaurantId) => {
  const result = await supabase
    .from('restaurant_settings')
    .select(SETTINGS_SELECT)
    .eq('restaurant_id', restaurantId)
    .maybeSingle();

  if (!result.error || !result.error.message?.includes('does not exist')) {
    return result;
  }

  return supabase
    .from('restaurant_settings')
    .select(LEGACY_SETTINGS_SELECT)
    .eq('restaurant_id', restaurantId)
    .maybeSingle();
};

export const getCurrentRestaurantBrand = async () => {
  const restaurantId = localStorage.getItem('currentRestaurantId');
  const fallbackName = localStorage.getItem('currentRestaurantName') || DEFAULT_RESTAURANT_NAME;

  if (!restaurantId) {
    return buildBrand({
      name: fallbackName,
      displayName: fallbackName,
      publicOrderingTitle: fallbackName,
    });
  }

  const [{ data: restaurant, error: restaurantError }, { data: settings, error: settingsError }] = await Promise.all([
    supabase
    .from('restaurants')
    .select('name')
    .eq('id', restaurantId)
      .maybeSingle(),
    fetchRestaurantSettings(restaurantId),
  ]);

  if (restaurantError) {
    console.warn('Restaurant lookup failed:', restaurantError.message);
  }
  if (settingsError && settingsError.code !== '42P01') {
    console.warn('Restaurant settings lookup failed:', settingsError.message);
  }

  const baseName = restaurant?.name?.trim() || fallbackName;
  const displayName = settings?.display_name?.trim() || baseName;
  const branchName = settings?.branch_name?.trim() || baseName;
  const name = displayName;
  localStorage.setItem('currentRestaurantName', name);
  return buildBrand({
    name,
    displayName,
    branchName,
    phone: settings?.phone?.trim() || '',
    address: settings?.address?.trim() || '',
    logoUrl: settings?.logo_url?.trim() || '',
    logoStoragePath: settings?.logo_storage_path?.trim() || '',
    receiptFooter: settings?.receipt_footer?.trim() || DEFAULT_RECEIPT_FOOTER,
    publicOrderingTitle: settings?.public_ordering_title?.trim() || displayName,
    taxLabel: settings?.tax_label?.trim() || '',
    taxNumber: settings?.tax_number?.trim() || '',
    deliveryCharge: settings?.delivery_charge ?? DEFAULT_DELIVERY_CHARGE,
    storeOpenTime: settings?.store_open_time || DEFAULT_OPEN_TIME,
    storeCloseTime: settings?.store_close_time || DEFAULT_CLOSE_TIME,
    isStoreOpen: settings?.is_store_open !== false,
    tagline: settings?.public_tagline?.trim() || '',
    aboutText: settings?.public_about_text?.trim() || '',
    heroImages: Array.isArray(settings?.public_hero_images) ? settings.public_hero_images : [],
  });
};

export const getPublicRestaurantBrand = async (restaurantId) => {
  if (!restaurantId) {
    return buildBrand({ publicOrderingTitle: DEFAULT_RESTAURANT_NAME });
  }

  const { data, error } = await supabase
    .rpc('get_public_restaurant_settings', { p_restaurant_id: restaurantId })
    .maybeSingle();

  if (error) {
    console.warn('Public restaurant settings lookup failed:', error.message);
    return buildBrand({ publicOrderingTitle: DEFAULT_RESTAURANT_NAME });
  }

  const displayName = data?.display_name?.trim() || DEFAULT_RESTAURANT_NAME;
  return buildBrand({
    name: displayName,
    displayName,
    branchName: data?.branch_name?.trim() || '',
    phone: data?.phone?.trim() || '',
    address: data?.address?.trim() || '',
    logoUrl: data?.logo_url?.trim() || '',
    publicOrderingTitle: data?.public_ordering_title?.trim() || displayName,
    deliveryCharge: data?.delivery_charge ?? DEFAULT_DELIVERY_CHARGE,
    storeOpenTime: data?.store_open_time || DEFAULT_OPEN_TIME,
    storeCloseTime: data?.store_close_time || DEFAULT_CLOSE_TIME,
    isStoreOpen: data?.is_store_open !== false,
    tagline: data?.public_tagline?.trim() || '',
    aboutText: data?.public_about_text?.trim() || '',
    heroImages: Array.isArray(data?.public_hero_images) ? data.public_hero_images : [],
  });
};
